import { useState, useEffect } from "react";
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import api from "../services/api";

const COLORS = [
  "#ef4444","#3b82f6","#10b981","#f59e0b","#8b5cf6",
  "#14b8a6","#f97316","#6366f1","#22c55e","#e11d48","#06b6d4",
];

export default function DistrictDrilldownChart({ setTotalBeneficiaries }) {

  const [selectedBlock, setSelectedBlock] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);

  /* ============================
     SAME AS DistrictDashboard
  ============================ */

  const [data, setData] = useState([]);
  const total = data.reduce(
    (sum, row) => sum + Number(row.value || 0),
    0
  );
  const [fromMonth, setFromMonth] = useState(
    new Date().toISOString().slice(0, 7)
  );

  const [toMonth, setToMonth] = useState(
    new Date().toISOString().slice(0, 7)
  );
  useEffect(() => {
    setTotalBeneficiaries(total);
  }, [data]);
  /* ============================
     SAME AS DistrictDashboard
     re-fetch when dates change
  ============================ */

  useEffect(() => {
    loadData();
    setSelectedBlock(null);
    setSelectedDepartment(null);
  }, [fromMonth, toMonth]);

  const loadData = async () => {
    try {
      const res = await api.get(
        `/dashboard/district?from=${fromMonth}&to=${toMonth}&mode=month`
      );
      setData(res.data);
    } catch (err) {
      console.log(err);
    }
  };

  /* ============================
     NO CLIENT SIDE FILTERING
     API already returned correct data
  ============================ */

  /* BLOCK LEVEL */

  const blockTotals = {};

  data.forEach((d) => {
    blockTotals[d.block] =
      (blockTotals[d.block] || 0) + Number(d.value);
  });

  const blockChart = Object.keys(blockTotals)
    .map((block) => ({ name: block, value: blockTotals[block] }))
    .filter((d) => d.value > 0);

  /* DEPARTMENT LEVEL */

  let departmentChart = [];

  if (selectedBlock) {
    const deptTotals = {};

    data
      .filter((d) => d.block === selectedBlock)
      .forEach((d) => {
        deptTotals[d.department] =
          (deptTotals[d.department] || 0) + Number(d.value);
      });

    departmentChart = Object.keys(deptTotals)
      .map((dep) => ({ name: dep, value: deptTotals[dep] }))
      .filter((d) => d.value > 0);
  }

  /* SCHEME LEVEL */

  let schemeChart = [];

  if (selectedBlock && selectedDepartment) {
    const schemeTotals = {};

    data
      .filter(
        (d) =>
          d.block === selectedBlock &&
          d.department === selectedDepartment
      )
      .forEach((d) => {
        schemeTotals[d.scheme] =
          (schemeTotals[d.scheme] || 0) + Number(d.value);
      });

    schemeChart = Object.keys(schemeTotals)
      .map((s) => ({ name: s, value: schemeTotals[s] }))
      .filter((d) => d.value > 0);
  }

  /* ============================
     RENDER PIE
  ============================ */

  const renderPie = (chartData, onClick) => {
    if (!chartData.length) {
      return (
        <p className="text-gray-500 text-center mt-10">
          No data available
        </p>
      );
    }

    return (
      <div className="w-full h-[420px]">
        <ResponsiveContainer>
          <PieChart>
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="name"
              outerRadius="75%"
              label={({ value }) => value}
              onClick={(d) => onClick && onClick(d.name)}
            >
              {chartData.map((entry, index) => (
                <Cell key={index} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  };

  /* ============================
     UI — SAME FILTER UI AS DistrictDashboard
  ============================ */

  return (
    <div className="bg-white shadow rounded p-6">

      {/* SAME FILTER AS DistrictDashboard */}

      <div className="flex gap-4 mb-4 flex-wrap items-center">

        <div>
          <label className="text-sm text-gray-600">From Month</label>
          <input
            type="month"
            value={fromMonth}
            onChange={(e) => setFromMonth(e.target.value)}
            className="border p-2 rounded ml-2"
          />
        </div>

        <div>
          <label className="text-sm text-gray-600">To Month</label>
          <input
            type="month"
            value={toMonth}
            onChange={(e) => setToMonth(e.target.value)}
            className="border p-2 rounded ml-2"
          />
        </div>

      </div>

      <h2 className="text-xl font-bold mb-3">
        District Beneficiary Distribution
      </h2>

      {!selectedBlock &&
        renderPie(blockChart, setSelectedBlock)}

      {selectedBlock && !selectedDepartment && (
        <>
          <button
            onClick={() => setSelectedBlock(null)}
            className="text-blue-600 mb-2"
          >
            ← Back to Blocks
          </button>
          {renderPie(departmentChart, setSelectedDepartment)}
        </>
      )}

      {selectedDepartment && (
        <>
          <button
            onClick={() => setSelectedDepartment(null)}
            className="text-blue-600 mb-2"
          >
            ← Back to Departments
          </button>
          {renderPie(schemeChart)}
        </>
      )}

    </div>
  );
}